import { type Highlight } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import React, { useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/src/designSystem/components/Button'
import { Text } from '@/src/designSystem/components/Text'

const VISIBLE_FRAGMENT_COUNT = 2

const SlotContainer = styled.div<{ $clickable: boolean }>`
  display: flex;
  gap: 0.75rem;
  border-radius: 0.375rem;
  background-color: var(--muted);
  padding: 0.5rem;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: ${(props) =>
    props.$clickable ? 'background-color 150ms' : 'none'};

  &:hover {
    background-color: ${(props) =>
      props.$clickable
        ? 'color-mix(in oklch, var(--muted) 80%, transparent)'
        : 'var(--muted)'};
  }
`

const AccentBar = styled.div`
  width: 0.25rem;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 9999px;
  background-color: var(--primary);
  opacity: 0.7;
`

const FragmentContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 0.125rem;
`

const FragmentText = styled.div`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--foreground);

  mark {
    border-radius: 0.125rem;
    background-color: color-mix(in oklch, var(--primary) 20%, transparent);
    padding-inline: 0.125rem;
  }
`

const HighlightGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const BlueLinkButton = styled(Button)`
  color: #60a5fa;
`

function HighlightSlot({
  fragment,
  pageNum,
  onFragmentClick,
}: {
  fragment: string
  pageNum: number
  onFragmentClick?: (pageNum: number, fragmentHtml: string) => void
}) {
  return (
    <SlotContainer
      $clickable={!!onFragmentClick}
      onClick={() => onFragmentClick?.(pageNum, fragment)}
      title={onFragmentClick ? 'Click to open and highlight' : undefined}>
      <AccentBar />
      <FragmentContent>
        <FragmentText dangerouslySetInnerHTML={{ __html: fragment }} />
        <Text as='p' variant='caption' color='muted' leading='tight'>
          Page {pageNum}
        </Text>
      </FragmentContent>
    </SlotContainer>
  )
}

export const PageHighlightGroup: React.FC<{
  highlight: Highlight
  onFragmentClick?: (pageNum: number, fragmentHtml: string) => void
}> = ({ highlight, onFragmentClick }) => {
  const [showAll, setShowAll] = useState(false)

  const hasMore = highlight.fragments.length > VISIBLE_FRAGMENT_COUNT
  const visibleFragments = showAll
    ? highlight.fragments
    : highlight.fragments.slice(0, VISIBLE_FRAGMENT_COUNT)
  const hiddenCount = highlight.fragments.length - VISIBLE_FRAGMENT_COUNT

  return (
    <HighlightGroup>
      {visibleFragments.map((fragment, idx) => (
        <HighlightSlot
          key={idx}
          fragment={fragment}
          pageNum={highlight.page_num}
          onFragmentClick={onFragmentClick}
        />
      ))}
      {hasMore && (
        <BlueLinkButton
          type='button'
          variant='link'
          size='inline'
          onClick={() => setShowAll((prev) => !prev)}>
          {showAll ? 'Show less' : `Show ${hiddenCount} more`}
        </BlueLinkButton>
      )}
    </HighlightGroup>
  )
}
